import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../axiosConfig";
import { ITask, IVerifyTaskPayload } from "./store.types";

export interface ITasksState {
  isLoading: boolean;
  outstanding: ITask[] | null;
  inProgress: ITask[] | null;
  completed: ITask[] | null;
  completedPagination: any;
  outstandingPagination: any;
  inProgressPagination: any;
  selectedTasks: boolean;
  updatedTasks: boolean;
  hasError: boolean;
}

export const NAME = "tasks";

const initialState: ITasksState = {
  isLoading: false,
  outstanding: null,
  inProgress: null,
  completed: null,
  completedPagination: null,
  outstandingPagination: null,
  inProgressPagination: null,
  selectedTasks: false,
  updatedTasks: false,
  hasError: false,
};

export const getOutstandingTasks = createAsyncThunk(
  `${NAME}/getOutstandingTasks`,
  async (args: any, thunkAPI) => {
    try {
      const response = await axiosInstance.post(`tasks`, args);
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const getInProgressTasks = createAsyncThunk(
  `${NAME}/getInProgressTasks`,
  async (args: any, thunkAPI) => {
    try {
      const response = await axiosInstance.post(`tasks`, args);
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const getCompletedTasks = createAsyncThunk(
  `${NAME}/getCompletedTasks`,
  async (args: any, thunkAPI) => {
    try {
      const response = await axiosInstance.post(`tasks`, args);
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const putVarifiedTasks = createAsyncThunk(
  `${NAME}/putVarifiedTasks`,
  async (args: IVerifyTaskPayload, thunkAPI) => {
    try {
      const response = await axiosInstance.put(`tasks`, args);

      thunkAPI.dispatch(getOutstandingTasks({ skip: 0, limit: 10 }));
      thunkAPI.dispatch(getInProgressTasks({ skip: 0, limit: 10 }));
      thunkAPI.dispatch(getCompletedTasks({ skip: 0, limit: 10 }));
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

const reducers = {
  setSelectedTasks: (state: ITasksState, { payload }) => {
    state.selectedTasks = payload;
  },
  setUpdatedTasks: (state: ITasksState, { payload }) => {
    state.updatedTasks = payload;
  },
};

export const tasksSlice = createSlice({
  name: NAME,
  initialState,
  reducers,
  extraReducers: (builder) => {
    builder.addCase(getOutstandingTasks.pending, (state) => {
      state.isLoading = true;
      state.outstanding = null;

      state.outstandingPagination = null;
      state.hasError = false;
      //
    });
    builder.addCase(getOutstandingTasks.fulfilled, (state, action) => {
      state.isLoading = false;
      state.outstanding = action.payload.data.results as ITask[];
      state.outstandingPagination = action.payload.data.pagination;
      state.hasError = false;
      //
    });
    builder.addCase(getOutstandingTasks.rejected, (state) => {
      state.isLoading = false;
      state.outstanding = null;
      state.outstandingPagination = null;
      state.hasError = true;
      //
    });
    builder.addCase(getInProgressTasks.pending, (state) => {
      state.isLoading = true;
      state.inProgress = null;

      state.inProgressPagination = null;
      state.hasError = false;
      //
    });
    builder.addCase(getInProgressTasks.fulfilled, (state, action) => {
      state.isLoading = false;
      state.inProgress = action.payload.data.results as ITask[];
      state.inProgressPagination = action.payload.data.pagination;
      state.hasError = false;
      //
    });
    builder.addCase(getInProgressTasks.rejected, (state) => {
      state.isLoading = false;
      state.inProgress = null;
      state.inProgressPagination = null;
      state.hasError = true;
      //
    });
    builder.addCase(getCompletedTasks.pending, (state) => {
      state.isLoading = true;
      state.completed = null;
      state.completedPagination = null;
      state.hasError = false;
      //
    });
    builder.addCase(getCompletedTasks.fulfilled, (state, action) => {
      state.isLoading = false;
      state.completed = action.payload.data.results as ITask[];
      state.completedPagination = action.payload.data.pagination;
      state.hasError = false;
      //
    });
    builder.addCase(getCompletedTasks.rejected, (state) => {
      state.isLoading = false;
      state.completed = null;
      state.completedPagination = null;
      state.hasError = true;
      //
    });
    builder.addCase(putVarifiedTasks.pending, (state) => {
      state.isLoading = true;
      state.hasError = false;
      //
    });
    builder.addCase(putVarifiedTasks.fulfilled, (state) => {
      state.isLoading = false;
      state.hasError = false;
      //
    });
    builder.addCase(putVarifiedTasks.rejected, (state) => {
      state.isLoading = false;
      state.hasError = true;
      //
    });
  },
});
export const selectIsLoading = (state) => state[NAME].isLoading;
export const selectOutstandingTasks = (state) => state[NAME].outstanding;
export const selectInProgressTasks = (state) => state[NAME].inProgress;
export const selectCompletedTasks = (state) => state[NAME].completed;
export const selectCompletedPagination = (state) =>
  state[NAME].completedPagination;
export const selectInProgressPagination = (state) =>
  state[NAME].inProgressPagination;
export const selectOutstandingPagination = (state) =>
  state[NAME].outstandingPagination;

export const selectTasks = (state) => state[NAME].selectedTasks;
export const updateTasks = (state) => state[NAME].updatedTasks;
export const { setSelectedTasks } = tasksSlice.actions;
export const { setUpdatedTasks } = tasksSlice.actions;

export default tasksSlice.reducer;
